import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Statistic,
  Spin, message, Table, DatePicker, Tabs, Alert, Divider, Tooltip, Empty
} from 'antd';
import {
  DownloadOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  SafetyOutlined,
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleFilled,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import StaffLayout from './StaffLayout';

const { Title, Text, Paragraph } = Typography;

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
};

// Friendly labels for earnings & deductions
const formatLabel = (key) => {
  const map = {
    basic_salary: 'Basic Salary',
    basicSalary: 'Basic Salary',
    hra: 'House Rent Allowance (HRA)',
    da: 'Dearness Allowance (DA)',
    special_allowance: 'Special Allowance',
    specialAllowance: 'Special Allowance',
    conveyance_allowance: 'Conveyance Allowance',
    conveyanceAllowance: 'Conveyance Allowance',
    medical_allowance: 'Medical Allowance',
    medicalAllowance: 'Medical Allowance',
    telephone_allowance: 'Telephone Allowance',
    telephoneAllowance: 'Telephone Allowance',
    other_allowances: 'Other Allowances',
    otherAllowances: 'Other Allowances',
    hospitality_allowance: 'Hospitality Allowance',
    overtimePay: 'Overtime Pay',
    earlyOvertimePay: 'Early Overtime Pay',
    provident_fund: 'Provident Fund (PF)',
    pfDeduction: 'Provident Fund (PF)',
    esi: 'Employee State Insurance (ESI)',
    esiDeduction: 'Employee State Insurance (ESI)',
    professional_tax: 'Professional Tax (PT)',
    professionalTax: 'Professional Tax (PT)',
    tds: 'Tax Deducted at Source (TDS)',
    tdsDeduction: 'Tax Deducted at Source (TDS)',
    loan_deduction: 'Advance Recovery',
    advanceDeduction: 'Advance Salary Deduction',
    latePenalty: 'Late Arrival Penalty',
    other_deductions: 'Other Deductions',
    otherDeductions: 'Other Deductions'
  };
  if (map[key]) return map[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const StaffSalary = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('breakdown');

  // Salary data state
  const [salaryData, setSalaryData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [earliestMonth, setEarliestMonth] = useState(null);
  const [salaryAccess, setSalaryAccess] = useState(true);

  // Month navigation boundary
  const currentMonth = useMemo(() => dayjs().startOf('month'), []);
  const isCurrentMonth = selectedMonth.isSame(currentMonth, 'month');
  const canGoNext = selectedMonth.isBefore(currentMonth, 'month');

  // Fetch earliest available month limit
  useEffect(() => {
    const fetchRange = async () => {
      try {
        const res = await api.get('/me/salary-history-range');
        if (res.data?.success && res.data.earliestMonth) {
          setEarliestMonth(dayjs(res.data.earliestMonth));
        }
      } catch (e) {
        console.error('Error fetching salary range:', e);
      }
    };
    fetchRange();
  }, []);

  const canGoPrev = useMemo(() => {
    if (!earliestMonth) {
      return selectedMonth.isAfter(dayjs().subtract(3, 'year').startOf('year'), 'month');
    }
    return selectedMonth.isAfter(earliestMonth, 'month');
  }, [selectedMonth, earliestMonth]);

  // Fetch salary computation for the selected month
  const fetchSalary = useCallback(async (month) => {
    setLoading(true);
    try {
      const monthKey = month.format('YYYY-MM');

      // 1. Check access
      try {
        const acc = await api.get('/me/salary/access');
        if (acc.data?.success && acc.data.allowCurrentCycle === false && month.isSame(currentMonth, 'month')) {
          setSalaryAccess(false);
          setSalaryData(null);
          setLoading(false);
          return;
        }
      } catch (_) {
        setSalaryAccess(true);
      }

      // 2. Compute salary data
      const [compRes, meRes] = await Promise.all([
        api.get(`/me/salary-compute?monthKey=${monthKey}`),
        api.get('/me')
      ]);

      if (meRes.data?.success && meRes.data.user) {
        setUserData(meRes.data.user);
      }

      if (compRes.data?.success) {
        const d = compRes.data;
        const e = d.earnings || {};
        const ded = d.deductions || {};
        const inc = d.incentives || {};
        const t = d.totals || {};
        const s = d.attendanceSummary || {};

        const sumObj = (obj) => Object.values(obj || {}).reduce((acc, v) => acc + (Number(v) || 0), 0);
        const totalEarnings = Number(t.totalEarnings ?? sumObj(e));
        const totalIncentives = Number(t.totalIncentives ?? sumObj(inc));
        const totalDeductions = Number(t.totalDeductions ?? sumObj(ded));
        const grossSalary = Number(t.grossSalary ?? (totalEarnings + totalIncentives));
        const netSalary = Number(t.netSalary ?? (grossSalary - totalDeductions));

        setSalaryData({
          earnings: e,
          deductions: ded,
          incentives: inc,
          totalEarnings,
          totalIncentives,
          totalDeductions,
          grossSalary,
          netSalary,
          paymentStatus: d.paymentStatus || 'DUE',
          paidAmount: Number(d.paidAmount || 0),
          isGenerated: d.isGenerated === true,
          payslipPath: d.payslipPath,
          attendanceSummary: s,
          ratio: Number(t.ratio ?? s.ratio ?? 1),
          basicSalary: Number(e.basic_salary || e.basicSalary || 0),
          overtimePay: Number(s.overtimePay || 0),
          latePenalty: Number(s.latePenalty || 0)
        });
      } else {
        setSalaryData(null);
      }
    } catch (err) {
      console.error('Error fetching salary details:', err);
      message.error(err.response?.data?.message || 'Failed to load salary details');
      setSalaryData(null);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchSalary(selectedMonth);
  }, [selectedMonth, fetchSalary]);

  // Month navigation
  const handlePrevMonth = () => {
    if (canGoPrev) setSelectedMonth((prev) => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    if (canGoNext) setSelectedMonth((prev) => prev.add(1, 'month'));
  };

  const handleCurrentMonth = () => {
    setSelectedMonth(dayjs());
  };

  // Download / View Payslip PDF
  const handleDownloadPayslip = async () => {
    try {
      setDownloading(true);
      const monthKey = selectedMonth.format('YYYY-MM');
      const token = localStorage.getItem('token');

      // If already generated by admin and direct path is available
      if (salaryData?.isGenerated && salaryData?.payslipPath) {
        const baseURL = api.defaults.baseURL || 'http://localhost:4000';
        const cleanPath = salaryData.payslipPath.startsWith('/') ? salaryData.payslipPath : `/${salaryData.payslipPath}`;
        const sep = cleanPath.includes('?') ? '&' : '?';
        const fullUrl = `${baseURL}${cleanPath}${token ? `${sep}token=${encodeURIComponent(token)}` : ''}`;
        window.open(fullUrl, '_blank');
        message.success('Opening official payslip...');
        return;
      }

      // Download PDF stream via /me/payslip
      const res = await api.get(`/me/payslip?monthKey=${monthKey}`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip-${monthKey}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Payslip downloaded successfully!');
    } catch (err) {
      console.error('Payslip download error:', err);
      message.error(err.response?.data?.message || 'Failed to download payslip');
    } finally {
      setDownloading(false);
    }
  };

  const daysInMonth = selectedMonth.daysInMonth();
  const summary = salaryData?.attendanceSummary || {};
  const payableDays = Number(summary.payableDays ?? ((Number(summary.present || 0) + Number(summary.half || 0) * 0.5 + Number(summary.weeklyOff || 0) + Number(summary.holidays || 0) + Number(summary.paidLeave || 0))));

  return (
    <StaffLayout>
      <div style={{ paddingBottom: 40 }}>
        {/* 1. HEADER CARD */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            marginBottom: 20,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <Space align="center" size={14}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb',
                fontSize: 24
              }}>
                <WalletOutlined />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                  {selectedMonth.format('MMMM YYYY')} Salary & Payslip
                </Title>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Monthly salary breakdown, pro-rata attendance calculations, and official payslips
                </Text>
              </div>
            </Space>

            <Space wrap size="middle">
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={(m) => m && setSelectedMonth(m)}
                allowClear={false}
                format="MMM YYYY"
                style={{ borderRadius: 8, height: 38 }}
              />

              <Button
                icon={<LeftOutlined />}
                onClick={handlePrevMonth}
                disabled={!canGoPrev}
                style={{ borderRadius: 8, height: 38 }}
              />
              <Button
                onClick={handleCurrentMonth}
                style={{ borderRadius: 8, height: 38, fontWeight: 500 }}
              >
                Current Month
              </Button>
              <Button
                icon={<RightOutlined />}
                onClick={handleNextMonth}
                disabled={!canGoNext}
                style={{ borderRadius: 8, height: 38 }}
              />

              {/* Quick Payslip Download Button */}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadPayslip}
                loading={downloading}
                disabled={loading || !salaryData}
                style={{
                  borderRadius: 8,
                  height: 38,
                  background: salaryData?.isGenerated ? '#16a34a' : '#2563eb',
                  borderColor: salaryData?.isGenerated ? '#16a34a' : '#2563eb',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.15)'
                }}
              >
                {salaryData?.isGenerated ? 'View Payslip' : 'Download Slip'}
              </Button>
            </Space>
          </div>
        </Card>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" tip="Calculating salary details..." />
          </div>
        ) : !salaryAccess ? (
          <Card style={{ borderRadius: 16, textAlign: 'center', padding: '40px 20px' }}>
            <SafetyOutlined style={{ fontSize: 48, color: '#f59e0b', marginBottom: 16 }} />
            <Title level={4}>Salary Access Restricted</Title>
            <Paragraph type="secondary">
              Viewing salary details for the current active payroll cycle is currently restricted by your organization administrator.
            </Paragraph>
          </Card>
        ) : !salaryData ? (
          <Card style={{ borderRadius: 16, textAlign: 'center', padding: '40px 20px' }}>
            <Empty description="No salary structure or payroll line found for this month" />
          </Card>
        ) : (
          <div>
            {/* 2. TOP METRIC SUMMARY CARDS */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
              marginBottom: 20
            }}>
              {/* Net Take-Home Pay */}
              <Card
                style={{
                  borderRadius: 16,
                  border: '1px solid #bbf7d0',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                }}
                bodyStyle={{ padding: '20px 22px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ color: '#15803d', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Net Salary
                  </Text>
                  <span style={{ fontSize: 20 }}>💰</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#14532d', lineHeight: 1.2 }}>
                  {formatCurrency(salaryData.netSalary)}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#166534', fontWeight: 500 }}>
                  Take-home amount after all deductions
                </div>
              </Card>

              {/* Gross Salary */}
              <Card
                style={{
                  borderRadius: 16,
                  border: '1px solid #bfdbfe',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                }}
                bodyStyle={{ padding: '20px 22px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ color: '#1d4ed8', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Gross Salary
                  </Text>
                  <span style={{ fontSize: 20 }}>💼</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#1e3a8a', lineHeight: 1.2 }}>
                  {formatCurrency(salaryData.grossSalary)}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#1e40af', fontWeight: 500 }}>
                  Total earnings + incentives
                </div>
              </Card>

              {/* Total Deductions */}
              <Card
                style={{
                  borderRadius: 16,
                  border: '1px solid #fecaca',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                }}
                bodyStyle={{ padding: '20px 22px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ color: '#b91c1c', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Total Deductions
                  </Text>
                  <span style={{ fontSize: 20 }}>📉</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#7f1d1d', lineHeight: 1.2 }}>
                  {formatCurrency(salaryData.totalDeductions)}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b', fontWeight: 500 }}>
                  PF, ESI, Tax, advances & penalties
                </div>
              </Card>

              {/* Payment Status & Payslip Lock */}
              <Card
                style={{
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                }}
                bodyStyle={{ padding: '20px 22px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ color: '#475569', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Payment Status
                  </Text>
                  <span style={{ fontSize: 20 }}>
                    {salaryData.paymentStatus === 'PAID' ? '✅' : '⏳'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <Tag
                    color={salaryData.paymentStatus === 'PAID' ? 'green' : 'blue'}
                    style={{ fontSize: 15, fontWeight: 800, padding: '4px 12px', borderRadius: 8 }}
                  >
                    {salaryData.paymentStatus === 'PAID' ? 'PAID' : 'DUE'}
                  </Tag>
                  {salaryData.isGenerated && (
                    <Tag color="cyan" style={{ fontSize: 11, fontWeight: 700, borderRadius: 6 }}>
                      Payslip Ready
                    </Tag>
                  )}
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
                  {salaryData.paymentStatus === 'PAID'
                    ? `Amount Paid: ${formatCurrency(salaryData.paidAmount || salaryData.netSalary)}`
                    : 'Disbursement pending from HR / Accounts'}
                </div>
              </Card>
            </div>

            {/* 3. PAYSLIP NOTICE BANNER */}
            <div style={{ marginBottom: 20 }}>
              {salaryData.isGenerated ? (
                <Alert
                  message={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                      <Space>
                        <CheckCircleFilled style={{ color: '#16a34a', fontSize: 18 }} />
                        <span style={{ fontSize: 13, color: '#14532d', fontWeight: 600 }}>
                          Official Payslip for {selectedMonth.format('MMMM YYYY')} has been generated and locked by Admin.
                        </span>
                      </Space>
                      <Button
                        type="primary"
                        size="small"
                        icon={<FilePdfOutlined />}
                        onClick={handleDownloadPayslip}
                        loading={downloading}
                        style={{ background: '#16a34a', borderColor: '#16a34a', borderRadius: 6, fontWeight: 600 }}
                      >
                        Open Official Payslip (PDF)
                      </Button>
                    </div>
                  }
                  type="success"
                  style={{ borderRadius: 12, border: '1px solid #bbf7d0', background: '#f0fdf4' }}
                />
              ) : (
                <Alert
                  message={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                      <Space>
                        <InfoCircleOutlined style={{ color: '#0284c7', fontSize: 18 }} />
                        <span style={{ fontSize: 13, color: '#0369a1', fontWeight: 500 }}>
                          Official payslip for {selectedMonth.format('MMMM YYYY')} is pending generation. Displaying live pro-rata salary computation.
                        </span>
                      </Space>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadPayslip}
                        loading={downloading}
                        style={{ borderRadius: 6, fontWeight: 500 }}
                      >
                        Download Estimate Slip
                      </Button>
                    </div>
                  }
                  type="info"
                  style={{ borderRadius: 12, border: '1px solid #bae6fd', background: '#f0f9ff' }}
                />
              )}
            </div>

            {/* 4. MAIN TABS: Breakdown & Payslip vs Attendance Pro-Rata vs CTC Structure */}
            <Card
              style={{
                borderRadius: 16,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
              }}
              bodyStyle={{ padding: 24 }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'breakdown',
                    label: (
                      <span style={{ fontWeight: 600 }}>
                        <WalletOutlined />
                        Earnings & Deductions Breakdown
                      </span>
                    ),
                    children: (
                      <div>
                        <Row gutter={[24, 24]}>
                          {/* EARNINGS COLUMN */}
                          <Col xs={24} lg={12}>
                            <div style={{
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: 14,
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                padding: '14px 20px',
                                background: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <Text strong style={{ fontSize: 14, color: '#0f172a' }}>
                                  Earnings Components
                                </Text>
                                <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>
                                  Pro-rated to {payableDays} days
                                </Tag>
                              </div>

                              <div style={{ padding: '8px 20px' }}>
                                {Object.entries(salaryData.earnings || {}).length === 0 ? (
                                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8' }}>
                                    No earnings configured
                                  </div>
                                ) : (
                                  Object.entries(salaryData.earnings || {}).map(([key, value]) => (
                                    <div
                                      key={key}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 0',
                                        borderBottom: '1px dashed #f1f5f9'
                                      }}
                                    >
                                      <div>
                                        <div style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                          {formatLabel(key)}
                                        </div>
                                      </div>
                                      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                                        {formatCurrency(value)}
                                      </div>
                                    </div>
                                  ))
                                )}

                                {/* Overtime / Incentives (if any) */}
                                {Number(salaryData.overtimePay) > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '12px 0',
                                      borderBottom: '1px dashed #f1f5f9'
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                        Overtime Allowance
                                      </div>
                                      <div style={{ fontSize: 11, color: '#64748b' }}>
                                        {summary.overtimeHours || 0} hrs worked
                                      </div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                                      {formatCurrency(salaryData.overtimePay)}
                                    </div>
                                  </div>
                                )}

                                {Object.entries(salaryData.incentives || {}).map(([key, value]) => (
                                  key !== 'total' && Number(value) > 0 && (
                                    <div
                                      key={key}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 0',
                                        borderBottom: '1px dashed #f1f5f9'
                                      }}
                                    >
                                      <div style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                        {formatLabel(key)}
                                      </div>
                                      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                                        {formatCurrency(value)}
                                      </div>
                                    </div>
                                  )
                                ))}
                              </div>

                              {/* Total Earnings Footer */}
                              <div style={{
                                padding: '14px 20px',
                                background: '#f8fafc',
                                borderTop: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <Text strong style={{ fontSize: 14, color: '#15803d' }}>
                                  Total Gross Earnings
                                </Text>
                                <span style={{ fontSize: 17, fontWeight: 800, color: '#15803d' }}>
                                  {formatCurrency(salaryData.grossSalary)}
                                </span>
                              </div>
                            </div>
                          </Col>

                          {/* DEDUCTIONS COLUMN */}
                          <Col xs={24} lg={12}>
                            <div style={{
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: 14,
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                padding: '14px 20px',
                                background: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <Text strong style={{ fontSize: 14, color: '#0f172a' }}>
                                  Deductions & Recoveries
                                </Text>
                                <Tag color="red" style={{ borderRadius: 6, fontWeight: 600 }}>
                                  Subtracted from Gross
                                </Tag>
                              </div>

                              <div style={{ padding: '8px 20px' }}>
                                {Object.entries(salaryData.deductions || {}).length === 0 && Number(salaryData.latePenalty) === 0 ? (
                                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#94a3b8' }}>
                                    No deductions recorded for this month
                                  </div>
                                ) : (
                                  Object.entries(salaryData.deductions || {}).map(([key, value]) => (
                                    <div
                                      key={key}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 0',
                                        borderBottom: '1px dashed #f1f5f9'
                                      }}
                                    >
                                      <div>
                                        <div style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                          {formatLabel(key)}
                                        </div>
                                      </div>
                                      <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 14 }}>
                                        - {formatCurrency(value)}
                                      </div>
                                    </div>
                                  ))
                                )}

                                {/* Late Penalty (if any) */}
                                {Number(salaryData.latePenalty) > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '12px 0',
                                      borderBottom: '1px dashed #f1f5f9'
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                        Late Arrival Penalty
                                      </div>
                                      <div style={{ fontSize: 11, color: '#64748b' }}>
                                        {summary.lateCount || 0} late punch-in(s)
                                      </div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 14 }}>
                                      - {formatCurrency(salaryData.latePenalty)}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Total Deductions Footer */}
                              <div style={{
                                padding: '14px 20px',
                                background: '#f8fafc',
                                borderTop: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <Text strong style={{ fontSize: 14, color: '#b91c1c' }}>
                                  Total Deductions
                                </Text>
                                <span style={{ fontSize: 17, fontWeight: 800, color: '#b91c1c' }}>
                                  - {formatCurrency(salaryData.totalDeductions)}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Net Salary Summary Strip */}
                        <div style={{
                          marginTop: 24,
                          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                          border: '1px solid #cbd5e1',
                          borderRadius: 14,
                          padding: '16px 24px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 16
                        }}>
                          <div>
                            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                              Net Payable Take-Home
                            </div>
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                              Gross ({formatCurrency(salaryData.grossSalary)}) − Deductions ({formatCurrency(salaryData.totalDeductions)})
                            </div>
                          </div>
                          <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>
                            {formatCurrency(salaryData.netSalary)}
                          </div>
                        </div>
                      </div>
                    )
                  },
                  {
                    key: 'attendance',
                    label: (
                      <span style={{ fontWeight: 600 }}>
                        <CalendarOutlined />
                        Attendance & Pro-Rata Impact
                      </span>
                    ),
                    children: (
                      <div>
                        {/* Attendance Metric Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                          gap: 12,
                          marginBottom: 24
                        }}>
                          {[
                            { label: 'Calendar Days', val: daysInMonth, color: '#2563eb', bg: '#eff6ff', icon: '📅' },
                            { label: 'Payable Days', val: payableDays, color: '#16a34a', bg: '#f0fdf4', icon: '✅' },
                            { label: 'Present Days', val: summary.present || 0, color: '#0891b2', bg: '#ecfeff', icon: '☀️' },
                            { label: 'Half Days', val: summary.half || 0, color: '#d97706', bg: '#fffbeb', icon: '⛅' },
                            { label: 'Weekly Offs', val: summary.weeklyOff || 0, color: '#6366f1', bg: '#eef2ff', icon: '☕' },
                            { label: 'Paid Leaves', val: summary.paidLeave || 0, color: '#8b5cf6', bg: '#f5f3ff', icon: '🏖️' },
                            { label: 'Holidays', val: summary.holidays || 0, color: '#db2777', bg: '#fdf2f8', icon: '🎉' },
                            { label: 'Absent Days', val: summary.absent || 0, color: '#dc2626', bg: '#fef2f2', icon: '❌' },
                          ].map((item) => (
                            <div
                              key={item.label}
                              style={{
                                background: item.bg,
                                borderRadius: 12,
                                padding: '14px 12px',
                                textAlign: 'center',
                                border: '1px solid rgba(0,0,0,0.04)'
                              }}
                            >
                              <div style={{ fontSize: 18 }}>{item.icon}</div>
                              <div style={{ fontSize: 20, fontWeight: 700, color: item.color, marginTop: 4 }}>
                                {item.val}
                              </div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginTop: 2 }}>
                                {item.label}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Calculation Formula Explanation Card */}
                        <Card
                          style={{
                            borderRadius: 12,
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0'
                          }}
                          bodyStyle={{ padding: 20 }}
                        >
                          <Title level={5} style={{ margin: 0, marginBottom: 8, color: '#0f172a' }}>
                            Pro-Rata Attendance Calculation Method
                          </Title>
                          <Paragraph style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
                            Your salary is calculated proportionally based on your total payable attendance days in {selectedMonth.format('MMMM YYYY')}.
                          </Paragraph>

                          <div style={{
                            background: '#ffffff',
                            padding: '14px 18px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            fontFamily: 'monospace',
                            fontSize: 13,
                            color: '#1e293b'
                          }}>
                            <div>• Monthly Working Base: <strong>{daysInMonth} calendar days</strong></div>
                            <div>• Total Payable Units: <strong>{payableDays} days</strong> (Present + Weekly Offs + Holidays + Paid Leaves + Half Days × 0.5)</div>
                            <div>• Attendance Ratio: <strong>{((Number(summary.ratio || (payableDays / daysInMonth))) * 100).toFixed(1)}%</strong></div>
                            <div>• Formula Applied: <strong>(Full Month Base Salary ÷ {daysInMonth}) × {payableDays} payable days</strong></div>
                          </div>
                        </Card>
                      </div>
                    )
                  },
                  {
                    key: 'template',
                    label: (
                      <span style={{ fontWeight: 600 }}>
                        <FileTextOutlined />
                        Assigned Salary Structure (CTC)
                      </span>
                    ),
                    children: (
                      <div>
                        <div style={{
                          background: '#f8fafc',
                          padding: '16px 20px',
                          borderRadius: 12,
                          border: '1px solid #e2e8f0',
                          marginBottom: 16,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 12
                        }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                              {userData?.salaryTemplate?.name || 'Standard Organization Salary Structure'}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              Base template assigned to your profile
                            </div>
                          </div>
                          <Tag color="purple" style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6 }}>
                            Official Structure
                          </Tag>
                        </div>

                        <Table
                          size="small"
                          pagination={false}
                          columns={[
                            { title: 'Component Type', dataIndex: 'type', key: 'type', render: (t) => <Tag color={t === 'Earning' ? 'blue' : 'red'}>{t}</Tag> },
                            { title: 'Component Name', dataIndex: 'name', key: 'name', render: (n) => <strong>{formatLabel(n)}</strong> },
                            { title: 'Calculation Mode', dataIndex: 'mode', key: 'mode', render: () => 'Pro-Rated Monthly' },
                          ]}
                          dataSource={[
                            ...Object.keys(salaryData.earnings || {}).map((k) => ({
                              key: `earn_${k}`,
                              type: 'Earning',
                              name: k
                            })),
                            ...Object.keys(salaryData.deductions || {}).map((k) => ({
                              key: `ded_${k}`,
                              type: 'Deduction',
                              name: k
                            }))
                          ]}
                          style={{ borderRadius: 8 }}
                        />
                      </div>
                    )
                  }
                ]}
              />
            </Card>
          </div>
        )}
      </div>
    </StaffLayout>
  );
};

export default StaffSalary;
